import { Button, Typography, useMediaQuery } from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';

import Checkbox from '@mui/material/Checkbox';
import { CustomEmailOptions } from '../models/CustomEmailOptions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/system/Unstable_Grid';
import { cloneDeep } from 'lodash';
import { useDomainOptions } from '../../dashboard/hooks/useDomainOptions';

interface CustomNotificationSettingProps {
  isSiteSetting?: boolean;
  emailOptions: CustomEmailOptions;
  handleChange: (emailOptions: CustomEmailOptions) => void;
}

type CustomEmailOptionType =
  | 'admin'
  | 'expiry'
  | 'content'
  | 'permissions'
  | 'assessment';

export default function CustomNotificationSetting(
  props: CustomNotificationSettingProps
) {
  const [currentEmailOptions, setCurrentEmailOptions] =
    useState<CustomEmailOptions>(props.emailOptions);

  const isMobile = useMediaQuery('(max-width: 900px)');

  //Get site setting custom email options
  const { siteEmailOptions } = useDomainOptions();

  useEffect(() => {
    setCurrentEmailOptions(props.emailOptions);
  }, [props.emailOptions]);

  const handleOptionChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const optionKey = event.target.value;
    const copy = cloneDeep(currentEmailOptions);

    Object.entries(copy).forEach(([key, value]) => {
      if (key === optionKey) {
        copy[key] = checked;
      }
    });
    setCurrentEmailOptions(copy);

    //Save the selected setting and email options if 'custom' setting selected
    props.handleChange(copy);
  };

  const getEmailOptionLabel = (key: string) => {
    const optionKey = key as CustomEmailOptionType;
    switch (optionKey) {
      case 'admin':
        return 'Personalised notifications, sent from an admin.';
      case 'expiry':
        return 'Notifications about your expiring and expired certificates.';
      case 'content':
        return 'Notifications regarding updates to your content.';
      case 'permissions':
        return 'Notifications in reference to changes to your site permissions.';
      case 'assessment':
        return 'Notifications concerning your formal assessments.';
    }
  };

  //Set site custom email options as default
  const handleReset = () => {
    setCurrentEmailOptions(siteEmailOptions);
    props.handleChange(siteEmailOptions);
  };

  if (!currentEmailOptions) {
    return null;
  }

  return (
    <>
      {Object.entries(currentEmailOptions).map(([key, value]) => (
        <Grid key={key}>
          <FormControlLabel
            key={key}
            value={key}
            control={
              <Checkbox
                sx={isMobile ? { padding: '3px 4px' } : { padding: '3px 8px' }}
                checked={value}
                onChange={handleOptionChange}
              />
            }
            label={
              <Typography variant={props.isSiteSetting ? 'body1' : 'body2'}>
                {getEmailOptionLabel(key)}
              </Typography>
            }
            labelPlacement="end"
          />
        </Grid>
      ))}
      <Grid>
        {!props.isSiteSetting && (
          <Button
            size="small"
            variant="text"
            sx={{ px: '0px' }}
            onClick={handleReset}
          >
            Set as default
          </Button>
        )}
      </Grid>
    </>
  );
}
